/**
 * Documentation retrieval client for the text-to-action pipeline.
 *
 * Fetches relevant documentation chunks for LLM-tier prompt enrichment.
 * Three-tier strategy:
 *
 *   1. **Backend** (`POST /services/agents/retrieve/`) — authoritative
 *      when Dan's RAG endpoint is deployed. Returns broader corpora
 *      including user-uploaded papers (once Theme I activates).
 *
 *   2. **Client-side fallback** (`clientRetrieve` over `bundledCorpus`)
 *      — covers the medchem distillates corpus. Always available;
 *      `gibbs-client-rag` study drops dependency on Dan's endpoint
 *      for the most-used path.
 *
 *   3. **Empty fallback** — caller continues without doc context.
 *
 * `clientFallbackPolicy` controls which path runs:
 *   - `'backend-only'` — old behavior; backend or nothing.
 *   - `'fallback'` (default) — backend first, client when backend
 *     unavailable / returns []. The right default while Dan's
 *     endpoint is not yet deployed.
 *   - `'merge'` — backend first, then append non-duplicate client
 *     chunks so the LLM sees both corpora. Right policy once
 *     backend deploys with broader content.
 *   - `'client-only'` — skip backend entirely. For tests and
 *     offline development.
 *
 * @module gibbs/text-to-action/docRetriever
 */

import { clientRetrieve, getCorpusMeta } from '../knowledge/clientRagRetriever';

/**
 * Default endpoint for the documentation retrieval service.
 * Can be overridden via setRetrieveEndpoint() for testing.
 */
let retrieveEndpoint = '/services/agents/retrieve/';

/**
 * Override the retrieve endpoint (for testing or alternative backends).
 *
 * @param {string} url
 */
export function setRetrieveEndpoint(url) {
    retrieveEndpoint = url;
}

/** Retrieval timeout in milliseconds. */
const RETRIEVE_TIMEOUT_MS = 2000;

/**
 * Current client fallback policy. Defaults to 'fallback' for the period
 * during which Dan's `/services/agents/retrieve/` is not deployed. Flip
 * to 'merge' once that endpoint is live and the merged-corpus policy
 * is desired.
 */
let clientFallbackPolicy = 'fallback';

/**
 * Override the fallback policy. Test / runtime tooling.
 *
 * @param {'backend-only'|'fallback'|'merge'|'client-only'} policy
 */
export function setClientFallbackPolicy(policy) {
    const valid = ['backend-only', 'fallback', 'merge', 'client-only'];
    if (!valid.includes(policy)) {
        // eslint-disable-next-line no-console
        console.warn(
            `[docRetriever] unknown clientFallbackPolicy "${policy}". `
            + `Keeping "${clientFallbackPolicy}".`
        );
        return;
    }
    clientFallbackPolicy = policy;
}

/**
 * Inspect the current fallback policy (test / debug).
 *
 * @returns {string}
 */
export function getClientFallbackPolicy() {
    return clientFallbackPolicy;
}

/**
 * Emit a one-time console warning when the bundled corpus is older
 * than 90 days. Dev hint: rebuild via `node proto/scripts/buildRagCorpus.js`.
 */
let staleCorpusWarned = false;
function warnIfCorpusStale() {
    if (staleCorpusWarned) return;
    const meta = getCorpusMeta();
    if (!meta.builtAt) return;
    const built = new Date(meta.builtAt);
    const ageMs = Date.now() - built.getTime();
    const ageDays = ageMs / (1000 * 60 * 60 * 24);
    if (ageDays > 90) {
        // eslint-disable-next-line no-console
        console.warn(
            `[docRetriever] bundled corpus is ${Math.floor(ageDays)} days `
            + 'old. Rebuild with `node proto/scripts/buildRagCorpus.js` '
            + 'to refresh.'
        );
        staleCorpusWarned = true;
    }
}

/**
 * Retrieve relevant documentation chunks for a user query.
 *
 * Strategy depends on `clientFallbackPolicy`. See module docstring.
 *
 * @param {string} userMessage - The user's natural language input
 * @param {string} tier - Pipeline tier ('command' | 'workflow')
 * @returns {Promise<Array<{text: string, source: string, score: number}>>}
 */
export async function retrieveDocs(userMessage, tier) {
    warnIfCorpusStale();

    if (clientFallbackPolicy === 'client-only') {
        return clientRetrieve(userMessage, { k: 3 });
    }

    const backendChunks = await tryBackend(userMessage, tier);

    if (clientFallbackPolicy === 'backend-only') {
        return backendChunks;
    }

    if (clientFallbackPolicy === 'fallback') {
        if (backendChunks.length > 0) return backendChunks;
        return clientRetrieve(userMessage, { k: 3 });
    }

    if (clientFallbackPolicy === 'merge') {
        const clientChunks = clientRetrieve(userMessage, { k: 3 });
        // De-duplicate by `text` substring overlap. Backend chunks come
        // first (authoritative); client chunks fill remaining slots.
        const seen = new Set(backendChunks.map((c) => c.text.slice(0, 80)));
        const additions = clientChunks.filter(
            (c) => !seen.has(c.text.slice(0, 80))
        );
        return [...backendChunks, ...additions].slice(0, 5);
    }

    return backendChunks;
}

/**
 * Try the backend retrieve endpoint with timeout + error handling.
 * Returns [] on any failure (caller decides fallback policy).
 */
async function tryBackend(userMessage, tier) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), RETRIEVE_TIMEOUT_MS);

    try {
        const response = await fetch(retrieveEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ query: userMessage, tier }),
            signal: controller.signal,
        });

        clearTimeout(timer);

        if (!response.ok) {
            // 404 (endpoint not deployed yet) is the expected path for
            // most of 2026-05 while Dan's deploy is pending — log at
            // info level to avoid console spam.
            if (response.status !== 404) {
                // eslint-disable-next-line no-console
                console.warn(
                    `[docRetriever] backend returned HTTP ${response.status}`
                );
            }
            return [];
        }

        const data = await response.json();
        return Array.isArray(data.chunks) ? data.chunks : [];
    } catch (err) {
        clearTimeout(timer);
        const isTimeout = err.name === 'AbortError';
        // eslint-disable-next-line no-console
        console.warn(
            `[docRetriever] ${isTimeout
                ? `backend timed out after ${RETRIEVE_TIMEOUT_MS}ms`
                : `backend error: ${err.message}`}`
        );
        return [];
    }
}
